import { MyProjects } from '@platform/client';
import { Card, Tabs, TabItem } from '@platform/components';
import Skeleton from 'react-loading-skeleton';
import { ProUserCard, ProUserSavedProjectCard } from './ProUserCard';
import { ProUserTeamCard } from './TeamCards';
import EmptyMyProjectsTabContent from './EmptyMyProjectsTab';
import {
  CardStatusEnum,
  ProCard,
  ProMyProjectsResponse,
  ProTeamCard,
} from '@platform/client/schemas';
import { ReactNode } from 'react';
import { useSearch } from 'wouter';

const ProUserMyProjects = ({ blockSearch = false }: { blockSearch?: boolean }) => {
  const projectData = MyProjects.useGetProUserMyProjects();

  const urlSearchParams = new URLSearchParams(useSearch());
  let selectedTab = urlSearchParams.get('tab');
  const paramMatchGroupId = parseInt(urlSearchParams.get('matchGroupId') ?? '');
  if (paramMatchGroupId && projectData.data) {
    selectedTab = findTabByMatchGroupId(paramMatchGroupId, projectData.data);
  }

  if (!projectData.isSuccess) {
    return (
      <div className='p-10 flex flex-col md:w-full max-w-[910px] pb-12 md:pb-36 gap-6'>
        {[1, 2, 3].map((_, index) => (
          <div key={index}>
            <Card
              headerContent='&nbsp;'
              headerColor='gray-50'
              buttonText=''
              buttonDisabled={true}
              content={<Skeleton className='w-48' />}
              contentSubText='&nbsp;'
              avatarSize='md'
              badges={[]}
              variant='small'
              buttonOnClick={() => {}}
            />
          </div>
        ))}
      </div>
    );
  }

  const { actionRequired, inProgress, inactive, saved, teamCards } = projectData.data;
  const activeTeamCards = teamCards.filter(
    (teamCard) => teamCard.cardStatus !== CardStatusEnum.project_complete,
  );
  const completedTeamCards = teamCards.filter(
    (teamCard) => teamCard.cardStatus === CardStatusEnum.project_complete,
  );

  // Break all cards in to proper tab panels
  // All panels show empty state except Action Required, which only displays if relevant cards exist
  const inProgressTabPanel: TabItem = {
    id: 'In Progress',
    content:
      inProgress.length > 0 ?
        <div className='mt-12'>{[...createProCards(inProgress, 'in-progress')]}</div>
      : <EmptyMyProjectsTabContent tabKey='In Progress' userType='pro' blockSearch={blockSearch} />,
  };
  const inactiveTabPanel: TabItem = {
    id: 'Inactive',
    content:
      inactive.length > 0 || completedTeamCards.length > 0 ?
        <div className='mt-12'>
          {[
            ...createProCards(inactive, 'inactive'),
            ...createProTeamCards(completedTeamCards, 'completed-team-cards'),
          ]}
        </div>
      : <EmptyMyProjectsTabContent tabKey='Inactive' userType='pro' blockSearch={blockSearch} />,
  };
  const savedTabPanel: TabItem = {
    id: 'Saved',
    content:
      saved.length > 0 ?
        <div className='mt-12'>
          {saved.map((card, i) => (
            <div key={`saved-project-pro-${card.project.id}-${i}`} className='my-6'>
              <ProUserSavedProjectCard cardData={card} />
            </div>
          ))}
        </div>
      : <EmptyMyProjectsTabContent tabKey='Saved' userType='pro' blockSearch={blockSearch} />,
  };
  const tabPanelItems: TabItem[] = [inProgressTabPanel, inactiveTabPanel, savedTabPanel];
  if (actionRequired.length > 0 || activeTeamCards.length > 0) {
    const actionRequiredTabPanel: TabItem = {
      id: 'Action Required',
      content: (
        <div className='mt-12'>
          {[
            createProCards(actionRequired, 'action-required'),
            ...createProTeamCards(activeTeamCards, 'active-team-cards'),
          ]}
        </div>
      ),
      actionCount: actionRequired.length + activeTeamCards.length,
    };
    tabPanelItems.unshift(actionRequiredTabPanel);
  }

  return (
    <div>
      <div className='mt-4'>
        <Tabs
          title='Pro User My Projects'
          items={tabPanelItems}
          defaultSelectedKey={selectedTab ?? undefined}
          orientation='horizontal'
        />
      </div>
    </div>
  );
};

// Helpers to map the project cards to the proper Ui component for team vs individual
const createProCards = (proCards: ProCard[], key: string): ReactNode[] => {
  return proCards.map((card, i) => {
    return (
      <div key={`${key}-${i}`} className='my-6 flex justify-center'>
        <ProUserCard cardData={card} />
      </div>
    );
  });
};
const createProTeamCards = (teamCards: ProTeamCard[], key: string): ReactNode[] => {
  return teamCards.map((card, i) => {
    return (
      <div key={`${key}-${i}`} className='my-6 flex justify-center'>
        <ProUserTeamCard cardData={card} />
      </div>
    );
  });
};
const findTabByMatchGroupId = (matchGroupId: number, proCards: ProMyProjectsResponse) => {
  const matchGroupIdToTabMap: Record<number, string> = {};
  proCards.actionRequired.forEach((card) => {
    matchGroupIdToTabMap[card.matchGroupId] = 'Action Required';
  });
  proCards.inProgress.forEach((card) => {
    matchGroupIdToTabMap[card.matchGroupId] = 'In Progress';
  });
  proCards.inactive.forEach((card) => {
    matchGroupIdToTabMap[card.matchGroupId] = 'Inactive';
  });
  return matchGroupIdToTabMap[matchGroupId];
};

export default ProUserMyProjects;
